import React from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from './ThemeContext';
import AsciiStopwatch from './AsciiStopwatch';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const { theme, isAscii: isAnyAscii, toggleTheme, toggleAscii } = useTheme();

  const handleCreateJoinRoom = () => {
    navigate('/room-creation');
  };

  const handlePlaySolo = () => {
    const sessionId = uuidv4();
    navigate('/game', { state: { isMultiplayer: false, sessionId } });
  };

  const handleTutorial = () => {
    navigate('/tutorial');
  };

  const handleArcade = () => {
    navigate('/arcade');
  };

  const isDark = theme === 'dark';
  const isAscii = isAnyAscii && isDark;
  const isAsciiLight = isAnyAscii && !isDark;

  // Helpers: pick ascii-dark vs ascii-light values, fallback for non-ascii
  const ac = (dark, light, fallback) => isAscii ? dark : isAsciiLight ? light : fallback;
  // Effective combined theme string (for AsciiStopwatch prop)
  const effectiveTheme = isAnyAscii ? (isDark ? 'ascii' : 'ascii-light') : theme;

  const resolvedStyles = {
    container: {
      ...lightStyles.container,
      background: ac('#000000', '#e8e8e4', isDark ? 'transparent' : lightStyles.container.background),
    },
    welcomeSection: {
      ...lightStyles.welcomeSection,
      background: ac('#000000', '#f2f2ee', isDark ? 'rgba(8, 12, 26, 0.95)' : lightStyles.welcomeSection.background),
      boxShadow: ac(
        '0 0 0 2px #33ff33, 0 0 24px rgba(51,255,51,0.25)',
        '0 0 0 2px #444, 0 4px 16px rgba(0,0,0,0.12)',
        isDark ? '0 0 0 1px rgba(0,245,255,0.15), 0 8px 32px rgba(0,0,0,0.9)' : lightStyles.welcomeSection.boxShadow,
      ),
      border: ac('2px solid #33ff33', '2px solid #444444', isDark ? '1px solid rgba(0,245,255,0.15)' : 'none'),
      fontFamily: isAnyAscii ? '"Courier New", Courier, monospace' : undefined,
      borderRadius: isAnyAscii ? '0' : undefined,
    },
    gif: lightStyles.gif,
    logoText: {
      ...lightStyles.logoText,
      color: ac('#33ff33', '#1a1a1a', isDark ? '#00d4ff' : lightStyles.logoText.color),
      textShadow: ac('0 0 14px rgba(51,255,51,0.6)', 'none', isDark ? '0 0 20px rgba(0,212,255,0.4)' : 'none'),
      letterSpacing: isAnyAscii ? '0.08em' : isDark ? '0.02em' : 'normal',
      fontFamily: isAnyAscii ? '"Courier New", Courier, monospace' : undefined,
    },
    roomButton: {
      ...lightStyles.roomButton,
      background: ac('#000000', '#f2f2ee', isDark ? 'linear-gradient(45deg,rgba(40,20,0,0.9),rgba(60,30,0,0.9))' : lightStyles.roomButton.background),
      border: ac('1px solid #33ff33', '1px solid #444444', isDark ? '1px solid rgba(255,165,0,0.5)' : 'none'),
      color: ac('#33ff33', '#1a1a1a', isDark ? '#ffb347' : lightStyles.roomButton.color),
      boxShadow: ac('0 0 10px rgba(51,255,51,0.2)', 'none', isDark ? '0 0 14px rgba(255,120,0,0.25)' : lightStyles.roomButton.boxShadow),
      borderRadius: isAnyAscii ? '0' : lightStyles.roomButton.borderRadius,
      fontFamily: isAnyAscii ? '"Courier New", Courier, monospace' : undefined,
    },
    soloButton: {
      ...lightStyles.soloButton,
      background: ac('#000000', '#f2f2ee', isDark ? 'linear-gradient(45deg,rgba(0,35,25,0.9),rgba(0,55,40,0.9))' : lightStyles.soloButton.background),
      border: ac('1px solid #33ff33', '1px solid #444444', isDark ? '1px solid rgba(0,245,196,0.5)' : 'none'),
      color: ac('#33ff33', '#1a1a1a', isDark ? '#00f5c4' : lightStyles.soloButton.color),
      boxShadow: ac('0 0 10px rgba(51,255,51,0.2)', 'none', isDark ? '0 0 14px rgba(0,245,196,0.25)' : lightStyles.soloButton.boxShadow),
      borderRadius: isAnyAscii ? '0' : lightStyles.soloButton.borderRadius,
      fontFamily: isAnyAscii ? '"Courier New", Courier, monospace' : undefined,
    },
    tutorialButton: {
      ...lightStyles.tutorialButton,
      backgroundColor: ac('#000000', '#f2f2ee', isDark ? 'transparent' : lightStyles.tutorialButton.backgroundColor),
      border: ac('1px solid #33ff33', '1px solid #444444', isDark ? '1px solid rgba(255,107,157,0.5)' : 'none'),
      color: ac('#33ff33', '#1a1a1a', isDark ? '#ff6b9d' : lightStyles.tutorialButton.color),
      boxShadow: ac('0 0 8px rgba(51,255,51,0.25)', 'none', isDark ? '0 0 10px rgba(255,107,157,0.3)' : lightStyles.tutorialButton.boxShadow),
      borderRadius: isAnyAscii ? '0' : lightStyles.tutorialButton.borderRadius,
    },
    arcadeButton: {
      ...lightStyles.arcadeButton,
      background: ac('#000000', '#f2f2ee', isDark ? 'linear-gradient(45deg,rgba(60,0,80,0.9),rgba(80,0,100,0.9))' : lightStyles.arcadeButton.background),
      border: ac('1px solid #33ff33', '1px solid #444444', isDark ? '1px solid rgba(200,100,255,0.6)' : 'none'),
      color: ac('#33ff33', '#1a1a1a', isDark ? '#e040fb' : lightStyles.arcadeButton.color),
      boxShadow: ac('0 0 10px rgba(51,255,51,0.2)', 'none', isDark ? '0 0 18px rgba(200,100,255,0.35)' : lightStyles.arcadeButton.boxShadow),
      borderRadius: isAnyAscii ? '0' : lightStyles.arcadeButton.borderRadius,
      fontFamily: isAnyAscii ? '"Courier New", Courier, monospace' : lightStyles.arcadeButton.fontFamily,
    },
  };

  return (
    <div className="login-container" style={resolvedStyles.container}>
      <div style={resolvedStyles.welcomeSection}>
        <button
          onClick={handleTutorial}
          style={resolvedStyles.tutorialButton}
          aria-label="Tutorial"
        >
          ?
        </button>

        {/* Theme toggle: light ↔ dark */}
        <button
          onClick={toggleTheme}
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            width: '35px',
            height: '35px',
            borderRadius: isAnyAscii ? '0' : '50%',
            backgroundColor: isAscii ? 'transparent' : isAsciiLight ? '#e8e8e4' : isDark ? 'transparent' : '#f0f0f0',
            border: isAscii ? '1px solid rgba(51,255,51,0.5)' : isAsciiLight ? '1px solid #555' : isDark ? '1px solid rgba(0,245,255,0.4)' : '1px solid #ddd',
            color: isAscii ? '#33ff33' : isAsciiLight ? '#1a1a1a' : isDark ? '#00d4ff' : '#888',
            boxShadow: isAscii ? '0 0 8px rgba(51,255,51,0.2)' : isDark ? '0 0 8px rgba(0,245,255,0.2)' : 'none',
            fontFamily: isAnyAscii ? '"Courier New", Courier, monospace' : undefined,
            fontSize: '1.1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 1,
          }}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? '☀' : '☾'}
        </button>

        {/* ASCII toggle: terminal mode on/off */}
        <button
          onClick={toggleAscii}
          style={{
            position: 'absolute',
            top: '10px',
            left: '54px',
            width: '35px',
            height: '35px',
            borderRadius: '0',
            backgroundColor: isAnyAscii
              ? (isAscii ? '#33ff33' : '#1a1a1a')
              : isDark ? 'transparent' : '#f0f0f0',
            border: isAscii
              ? '1px solid #33ff33'
              : isAsciiLight
              ? '1px solid #444'
              : isDark
              ? '1px solid rgba(0,245,255,0.4)'
              : '1px solid #ddd',
            color: isAnyAscii
              ? (isAscii ? '#000' : '#f2f2ee')
              : isDark ? '#8ab4cc' : '#888',
            boxShadow: isAscii ? '0 0 10px rgba(51,255,51,0.3)' : 'none',
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: '0.85rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 1,
          }}
          aria-label={isAnyAscii ? 'Disable ASCII mode' : 'Enable ASCII mode'}
          title={isAnyAscii ? 'ASCII mode ON' : 'ASCII mode OFF'}
        >
          {'> _'}
        </button>

        {isAnyAscii ? (
          <AsciiStopwatch theme={effectiveTheme} />
        ) : (
          <img
            src={isDark ? "hititatzero_dark.gif" : "stopwatch_hititatzero.gif"}
            alt="Welcome to Hititatzero!"
            style={resolvedStyles.gif}
          />
        )}
        <h1 style={resolvedStyles.logoText}>Welcome to Hititatzero!</h1>

        <button onClick={handleArcade} style={resolvedStyles.arcadeButton}>
          {isAnyAscii ? '>> ARCADE MODE <<' : '🕹️ ARCADE MODE'}
        </button>
        <button onClick={handleCreateJoinRoom} style={resolvedStyles.roomButton}>
          🟠 Create / Join Room
        </button>
        <button onClick={handlePlaySolo} style={resolvedStyles.soloButton}>
          🟢 Play Solo
        </button>
      </div>
    </div>
  );
};

const lightStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#f8f8f4',
  },
  welcomeSection: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    padding: '20px',
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: '15px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
    minWidth: '300px',
  },
  gif: {
    width: '200px',
    borderRadius: '10px',
  },
  logoText: {
    fontSize: '2rem',
    color: '#ff6f61',
    margin: '20px 0',
    textAlign: 'center',
  },
  roomButton: {
    fontSize: '1.2rem',
    padding: '10px 20px',
    borderRadius: '15px',
    border: 'none',
    color: 'white',
    background: 'linear-gradient(45deg, #ffa726, #fb8c00)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    width: '250px',
  },
  soloButton: {
    fontSize: '1.2rem',
    padding: '10px 20px',
    borderRadius: '15px',
    border: 'none',
    color: 'white',
    background: 'linear-gradient(45deg, #66bb6a, #43a047)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    width: '250px',
  },
  arcadeButton: {
    fontSize: '1rem',
    padding: '10px 20px',
    borderRadius: '15px',
    border: '2px solid #9c27b0',
    color: 'white',
    background: 'linear-gradient(45deg, #7b1fa2, #4a148c)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: '0 4px 18px rgba(156,39,176,0.45)',
    width: '250px',
    fontFamily: "'Press Start 2P', 'Courier New', monospace",
    letterSpacing: '0.05em',
  },
  tutorialButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    backgroundColor: '#ff6f61',
    color: 'white',
    fontSize: '1.1rem',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    zIndex: 1,
  },
};

export default Login;
